import logoPremio from 'assets/img/2026/logo_premio.png';
import fotoPadraoBB from 'assets/img/2026/new_foto_padrao_bb.png';
import { useAuth } from 'context/AuthContext';
import { useActivePhases } from 'hooks/useControlDate';
import { useMostrarMenuPreInscricao } from 'hooks/useMostrarMenuPreInscricao';
import { useEffect, useState } from 'react';
import { HiLogout, HiMenu, HiX } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { canAccessMinhasIniciativas as canAccessMinhasIniciativasByRule } from 'services/minhas-iniciativas-eligibility.service';
import { isBancaUser } from 'utils/auth';
import './style.scss';

interface HeaderProps {
  currentPage?: 'inicio' | 'iniciativas' | 'classificadas-etapa-1' | 'meus-votos' | 'minha-jornada' | 'minhas-iniciativas' | 'regulamento' | 'avaliacao-etapa-2' | 'votacao-etapa-2' | 'votacao-etapa-3' | 'meus-votos-etapa-2' | 'meus-votos-etapa-3' | 'pre-inscricao';
  canAccessMinhasIniciativas?: boolean;
  /** Quando true, exibe a flag amarela "Confira seu extrato!" abaixo do link Minha Jornada. */
  highlightMinhaJornada?: boolean;
}

export function Header({ currentPage = 'inicio', canAccessMinhasIniciativas = false, highlightMinhaJornada = false }: HeaderProps) {
  const { user, signOut } = useAuth();
  const { etapa2premio } = useActivePhases();
  const mostrarPreInscricao = useMostrarMenuPreInscricao();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [hasMinhasIniciativasAccess, setHasMinhasIniciativasAccess] = useState(false);
  const logoutPath = isBancaUser(user) ? 'login' : '/login';
  const isBancaTecnica = user?.extra?.etapa === 2 && user?.extra?.categories?.some((c: string) => c !== 'ELBB');

  useEffect(() => {
    let isMounted = true;

    async function loadMinhasIniciativasAccess() {
      if (!user?._id) {
        if (isMounted) {
          setHasMinhasIniciativasAccess(false);
        }
        return;
      }

      try {
        const canAccess = await canAccessMinhasIniciativasByRule(user._id);
        if (isMounted) {
          setHasMinhasIniciativasAccess(canAccess);
        }
      } catch {
        if (isMounted) {
          setHasMinhasIniciativasAccess(false);
        }
      }
    }

    loadMinhasIniciativasAccess();

    return () => {
      isMounted = false;
    };
  }, [user?._id]);

  const shouldShowMinhasIniciativas =
    canAccessMinhasIniciativas || hasMinhasIniciativasAccess || currentPage === 'minhas-iniciativas';

  // Não renderizar até que o usuário esteja carregado
  if (!user) return null;

  const getUserName = () => {
    return user.detail?.nome_guerra || user.detail?.nome || user.name || 'Usuário';
  };

  const getUserPhoto = () => user.image?.small?.url || fotoPadraoBB;

  return (
    <header className="home-header">
      <div className="home-header__yellow-detail" aria-hidden="true"></div>
      <div className="home-header__content">
        <div className="home-header__brand">
          <button
            className="home-header__menu-btn"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Fechar menu de navegação' : 'Abrir menu de navegação'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMenuOpen ? <HiX size={24} color="white" aria-hidden="true" /> : <HiMenu size={24} color="white" aria-hidden="true" />}
          </button>
          <img src={logoPremio} alt="Prêmio BB" className="home-header__logo" />
          <span className="home-header__brand-text" aria-hidden="true">Prêmio BB</span>
        </div>
        <div className="home-header__divider" aria-hidden="true"></div>
        <nav className="home-header__nav" aria-label="Navegação principal">
          <a
            href="/home-public"
            className={`home-header__link ${currentPage === 'inicio' ? 'home-header__link--active' : ''}`}
            aria-current={currentPage === 'inicio' ? 'page' : undefined}
          >
            Início
          </a>
          {mostrarPreInscricao && (
            <a
              href="/listagem"
              className={`home-header__link ${currentPage === 'pre-inscricao' ? 'home-header__link--active' : ''}`}
              aria-current={currentPage === 'pre-inscricao' ? 'page' : undefined}
            >
              Pré-inscrição
            </a>
          )}
          <a
            href="/minha-jornada"
            className={`home-header__link ${currentPage === 'minha-jornada' ? 'home-header__link--active' : ''}${highlightMinhaJornada ? ' home-header__link--flagged' : ''}`}
            aria-current={currentPage === 'minha-jornada' ? 'page' : undefined}
          >
            Minha Jornada
            {highlightMinhaJornada && (
              <span className="home-header__link-flag" aria-hidden="true">
                Confira seu extrato!
              </span>
            )}
          </a>
          {shouldShowMinhasIniciativas && (
            <a
              href="/minhas-iniciativas"
              className={`home-header__link ${currentPage === 'minhas-iniciativas' ? 'home-header__link--active' : ''}`}
              aria-current={currentPage === 'minhas-iniciativas' ? 'page' : undefined}
            >
              Minhas Iniciativas
            </a>
          )}
         {user.detail?.etapa1 && <a
            href="/meus-votos"
            className={`home-header__link ${currentPage === 'meus-votos' ? 'home-header__link--active' : ''}`}
            aria-current={currentPage === 'meus-votos' ? 'page' : undefined}
          >
            Seleção Etapa 1
          </a>}
          {isBancaTecnica && etapa2premio && (
            <a
              href="/votacao-etapa-2"
              className={`home-header__link ${currentPage === 'avaliacao-etapa-2' ? 'home-header__link--active' : ''}`}
              aria-current={currentPage === 'avaliacao-etapa-2' ? 'page' : undefined}
            >
              Avaliação Etapa 2
            </a>
          )}
          {isBancaTecnica && etapa2premio && (
            <Link
              to="/meus-votos-etapa-2"
              className={`home-header__link ${currentPage === 'meus-votos-etapa-2' ? 'home-header__link--active' : ''}`}
              aria-current={currentPage === 'meus-votos-etapa-2' ? 'page' : undefined}
            >
              Meus votos Etapa 2
            </Link>
          )}
          <a
            href="/regulamento"
            className={`home-header__link ${currentPage === 'regulamento' ? 'home-header__link--active' : ''}`}
            aria-current={currentPage === 'regulamento' ? 'page' : undefined}
          >
            Regulamento
          </a>
        </nav>
        <div className="home-header__user">
          <span className="home-header__username" tabIndex={0} aria-label={`Usuário: ${getUserName()}`}>{getUserName().toUpperCase()}</span>
          <div className="home-header__avatar" role="img" aria-label={`Foto de perfil de ${getUserName()}`}>
            <img
              src={getUserPhoto()}
              alt=""
              onError={(event) => {
                if (event.currentTarget.src !== fotoPadraoBB) {
                  event.currentTarget.src = fotoPadraoBB;
                }
              }}
            />
          </div>
          <button
            className="home-header__logout-btn"
            aria-label="Sair da conta"
            onClick={() => signOut(logoutPath)}
          >
            <HiLogout size={24} aria-hidden="true" />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        id="mobile-menu"
        className={`home-header__mobile-menu ${isMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMenuOpen}
      >
        <nav className="home-header__mobile-nav-links" aria-label="Navegação mobile">
          <a
            href="/home-public"
            className={`home-header__mobile-link ${currentPage === 'inicio' ? 'active' : ''}`}
            aria-current={currentPage === 'inicio' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Início
          </a>
          {mostrarPreInscricao && (
            <a
              href="/listagem"
              className={`home-header__mobile-link ${currentPage === 'pre-inscricao' ? 'active' : ''}`}
              aria-current={currentPage === 'pre-inscricao' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Pré-inscrição
            </a>
          )}
          <a
            href="/minha-jornada"
            className={`home-header__mobile-link ${currentPage === 'minha-jornada' ? 'active' : ''}${highlightMinhaJornada ? ' home-header__mobile-link--flagged' : ''}`}
            aria-current={currentPage === 'minha-jornada' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Minha Jornada
            {highlightMinhaJornada && (
              <span className="home-header__mobile-link-flag">Confira seu extrato!</span>
            )}
          </a>
          {shouldShowMinhasIniciativas && (
            <a
              href="/minhas-iniciativas"
              className={`home-header__mobile-link ${currentPage === 'minhas-iniciativas' ? 'active' : ''}`}
              aria-current={currentPage === 'minhas-iniciativas' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Minhas Iniciativas
            </a>
          )}
          <a
            href="/meus-votos"
            className={`home-header__mobile-link ${currentPage === 'meus-votos' ? 'active' : ''}`}
            aria-current={currentPage === 'meus-votos' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Seleção Etapa 1
          </a>
          {isBancaTecnica && etapa2premio && (
            <a
              href="/votacao-etapa-2"
              className={`home-header__mobile-link ${currentPage === 'avaliacao-etapa-2' ? 'active' : ''}`}
              aria-current={currentPage === 'avaliacao-etapa-2' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Avaliação Etapa 2
            </a>
          )}
          {isBancaTecnica && etapa2premio && (
            <Link
              to="/meus-votos-etapa-2"
              className={`home-header__mobile-link ${currentPage === 'meus-votos-etapa-2' ? 'active' : ''}`}
              aria-current={currentPage === 'meus-votos-etapa-2' ? 'page' : undefined}
              tabIndex={isMenuOpen ? 0 : -1}
            >
              Meus votos Etapa 2
            </Link>
          )}
          <a
            href="/regulamento"
            className={`home-header__mobile-link ${currentPage === 'regulamento' ? 'active' : ''}`}
            aria-current={currentPage === 'regulamento' ? 'page' : undefined}
            tabIndex={isMenuOpen ? 0 : -1}
          >
            Regulamento
          </a>
        </nav>
      </div>
    </header>
  );
}
